/**
 * Dentis Price Override
 *
 * Listens for `dentis:discount-resolved` and mutates `[data-dentis-price]`
 * elements in place: strikes the original price and replaces the current-price
 * slot with the Shopify-computed discounted amount.
 *
 * Three rendering "styles":
 *   - default       (snippet `price.liquid`, `topmeta-price.liquid`):
 *       .price__regular (no compare) → wrap in <s>, then add <s class="dentis-discount-applied"> after
 *       .price__sale > <s class="price-item--regular">      → leave alone (Shopify sale strike)
 *       .price-item--sale span                               → override text
 *   - bundle        ([data-dentis-price-style="bundle"]):
 *       [data-dentis-price-compare] → leave alone (existing strike) OR add if missing
 *       [data-dentis-price-current] → override text
 *   - dpc           ([data-dentis-price-style="dpc"]):
 *       [data-dentis-price-compare] → leave alone OR add if missing (mirrors bundle)
 *       [data-dentis-price-current] → override text
 *
 * Silent fallback: any DOM mutation that throws is swallowed; node restored if possible.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'dentis:discount-resolved';
  var APPLIED_CLASS = 'dentis-discount-applied';
  var DBG = (window.DentisDebug || { enabled: false, log: function () {}, warn: function () {}, error: function () {} });

  // Format a numeric Shopify cents value to the same money pattern as Liquid's
  // `money_without_trailing_zeros` filter. We keep the locale-derived currency
  // glyph, rounding to integer kr in NO/DK/SE/FI markets and to the nearest
  // 0.05 / 0.01 elsewhere via Intl.NumberFormat fallback.
  function formatMoney(cents, sample) {
    if (cents == null || isNaN(cents)) return null;
    cents = Math.round(Number(cents) / 100) * 100;
    var amount = cents / 100;

    // Detect sample's thousands separator and decimal separator.
    function detectSeparators(str) {
      var hasComma = str.indexOf(',') > -1;
      var hasDot = str.indexOf('.') > -1;
      var hasSpace = /\s/.test(str.replace(/^\s+|\s+$/g, ''));
      // EU/Nordic: 1 299,99 or 1.299,99 → decimal=',', thousands=' ' or '.'
      // EN/US: 1,299.99 → decimal='.', thousands=','
      var decimal = '.';
      var thousands = ',';
      if (hasComma && hasDot) {
        if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
          decimal = ','; thousands = '.';
        } else {
          decimal = '.'; thousands = ',';
        }
      } else if (hasComma && !hasDot) {
        // Either Nordic decimal or English thousands. Look at digits after comma:
        // 2 digits = decimal; 3 digits = thousands.
        var afterComma = str.split(',').pop();
        if (/^\d{1,2}\b/.test(afterComma)) { decimal = ','; thousands = ' '; }
        else { decimal = '.'; thousands = ','; }
      } else if (hasDot && !hasComma) {
        var afterDot = str.split('.').pop();
        if (/^\d{1,2}\b/.test(afterDot)) { decimal = '.'; thousands = ' '; }
        else { decimal = ','; thousands = '.'; }
      } else if (hasSpace) {
        decimal = ','; thousands = ' ';
      }
      return { decimal: decimal, thousands: thousands };
    }

    function withThousands(intStr, sep) {
      return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }

    if (sample && typeof sample === 'string') {
      var match = sample.match(/(-?[\d\.\s,]+)/);
      if (match) {
        var seps = detectSeparators(match[1]);
        var unit = sample.replace(match[1], '').replace(/\s+/g, ' ').trim();
        var rendered;
        if (Math.abs(amount - Math.round(amount)) < 0.005) {
          rendered = withThousands(String(Math.round(amount)), seps.thousands);
        } else {
          var parts = amount.toFixed(2).split('.');
          rendered = withThousands(parts[0], seps.thousands) + seps.decimal + parts[1];
        }
        if (!unit) return rendered;
        if (sample.trim().indexOf(unit) === 0) return unit + ' ' + rendered;
        return rendered + ' ' + unit;
      }
    }

    // Fallback: integer kr (works for NO/DK/SE/FI/NL/DE).
    if (Math.abs(amount - Math.round(amount)) < 0.005) {
      return withThousands(String(Math.round(amount)), ' ') + ' kr';
    }
    return amount.toFixed(2).replace('.', ',') + ' kr';
  }

  function getDiscountForVariant(resolved, productId, variantId) {
    if (!resolved || !resolved.active) return null;
    if (resolved.type !== 'percentage' && resolved.type !== 'fixed') return null;
    var pid = String(productId || '');
    var vid = String(variantId || '');
    var bucket = resolved.eligible && resolved.eligible[pid];
    if (bucket) {
      var perVariant = bucket.variants && bucket.variants[vid];
      if (perVariant == null) {
        var first = null;
        for (var k in (bucket.variants || {})) { first = bucket.variants[k]; break; }
        perVariant = first;
      }
      if (perVariant != null) {
        return {
          perUnitSavingsCents: Number(perVariant),
          type: resolved.type,
          value: Number(resolved.value || 0)
        };
      }
    }
    // Heuristic: when Shopify's discount_application target_selection === 'all',
    // the code applies to every product. Trust that and let the per-variant
    // discount be computed from value/value_type.
    if (resolved.target_selection === 'all' && (resolved.target_type === 'line_item' || !resolved.target_type)) {
      return {
        perUnitSavingsCents: 0,
        type: resolved.type,
        value: Number(resolved.value || 0)
      };
    }
    return null;
  }

  function computeDiscountedPriceCents(basePriceCents, info) {
    if (!info) return null;
    if (info.perUnitSavingsCents > 0) {
      return Math.max(0, basePriceCents - info.perUnitSavingsCents);
    }
    // Defensive fallback: derive from value/value_type
    if (info.type === 'percentage' && info.value > 0) {
      return Math.max(0, Math.round(basePriceCents * (1 - info.value / 100)));
    }
    if (info.type === 'fixed' && info.value > 0) {
      // value is in shop currency units (kr); convert to cents
      return Math.max(0, basePriceCents - Math.round(info.value * 100));
    }
    return null;
  }

  function applyDefaultStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    var saleEl = node.querySelector('.price__sale');
    var regularEl = node.querySelector('.price__regular');
    var saleItem = node.querySelector('.price-item--sale');
    var sample = (saleItem && saleItem.textContent.trim())
                 || (regularEl && regularEl.querySelector('.price-item--regular') && regularEl.querySelector('.price-item--regular').textContent.trim())
                 || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;

    if (comparePriceCents > basePriceCents) {
      // Sale case — strike original (compare_at) is already there. Just override the sale text.
      if (saleItem) saleItem.textContent = formatted;
    } else {
      // Non-sale case — convert the regular display into a strike + discounted line.
      if (regularEl) {
        var inner = regularEl.querySelector('.price-item--regular');
        if (inner) {
          inner.innerHTML = '<s class="price-item price-item--regular price-item--regular-was">' + inner.textContent.trim() + '</s>'
            + ' <span class="price-item price-item--sale price-item--last ' + APPLIED_CLASS + '">' + formatted + '</span>';
        }
      }
      if (saleItem) saleItem.textContent = formatted;
    }
    node.classList.add(APPLIED_CLASS);
    node.classList.add('price--on-sale');
  }

  function applyBundleStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    var current = node.querySelector('[data-dentis-price-current]');
    var compare = node.querySelector('[data-dentis-price-compare]');
    var sample = (current && current.textContent.trim()) || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;
    if (current) current.textContent = formatted;

    // Ensure original price is shown as strike (use compare_at if present, else base)
    var strikeBaseCents = comparePriceCents > basePriceCents ? comparePriceCents : basePriceCents;
    var strikeFormatted = formatMoney(strikeBaseCents, sample);
    if (!compare && strikeFormatted) {
      compare = document.createElement('span');
      compare.className = 'dentis-bundle-picker__price-original';
      compare.setAttribute('data-dentis-price-compare', '');
      compare.textContent = strikeFormatted;
      if (current && current.parentNode) current.parentNode.insertBefore(compare, current);
    } else if (compare && comparePriceCents <= basePriceCents) {
      // No native sale, base becomes strike
      compare.textContent = strikeFormatted;
    }

    var bundleScope = (node.closest && node.closest('.dentis-bundle-picker__option')) || node;
    updateSavingsBadges(bundleScope, discountedCents);

    node.classList.add(APPLIED_CLASS);
  }

  function updateSavingsBadges(scope, discountedCents) {
    if (!scope || !scope.querySelectorAll) return;
    var badges = scope.querySelectorAll('[data-dentis-savings-badge]');
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var baseCents = parseInt(badge.getAttribute('data-dentis-savings-base-cents') || '0', 10);
      var compareCents = parseInt(badge.getAttribute('data-dentis-savings-compare-cents') || '0', 10);
      var format = badge.getAttribute('data-dentis-savings-format') || '-{percent}%';

      if (!badge.hasAttribute('data-dentis-savings-original')) {
        badge.setAttribute('data-dentis-savings-original', badge.textContent);
      }

      var effectiveCompare = compareCents > baseCents ? compareCents : baseCents;
      if (effectiveCompare <= 0 || discountedCents == null || discountedCents >= effectiveCompare) {
        badge.style.display = 'none';
        continue;
      }

      var percent = Math.round((effectiveCompare - discountedCents) / effectiveCompare * 100);
      if (percent <= 0) {
        badge.style.display = 'none';
        continue;
      }

      badge.textContent = format.replace('{percent}', String(percent));
      badge.style.display = '';
    }
  }

  function applyDpcStyle(node, basePriceCents, comparePriceCents, discountedCents) {
    var current = node.querySelector('[data-dentis-price-current]');
    var compare = node.querySelector('[data-dentis-price-compare]');
    var sample = (current && current.textContent.trim()) || '';
    var formatted = formatMoney(discountedCents, sample);
    if (!formatted) return;
    if (current) current.textContent = formatted;

    var strikeBaseCents = comparePriceCents > basePriceCents ? comparePriceCents : basePriceCents;
    var strikeFormatted = formatMoney(strikeBaseCents, sample);
    if (!compare && strikeFormatted) {
      compare = document.createElement('span');
      compare.className = 'dpc-flex__value-secondary';
      compare.setAttribute('data-dentis-price-compare', '');
      compare.textContent = strikeFormatted;
      if (current && current.parentNode) current.parentNode.insertBefore(compare, current);
    } else if (compare && comparePriceCents <= basePriceCents) {
      compare.textContent = strikeFormatted;
    }
    node.classList.add(APPLIED_CLASS);
  }

  function processNode(node, resolved) {
    if (!node || node.classList.contains(APPLIED_CLASS)) return;
    var basePrice = parseInt(node.getAttribute('data-dentis-base-price') || '0', 10);
    var comparePrice = parseInt(node.getAttribute('data-dentis-compare-price') || '0', 10);
    var productId = node.getAttribute('data-dentis-product-id');
    var variantId = node.getAttribute('data-dentis-variant-id');
    var style = node.getAttribute('data-dentis-price-style') || 'default';
    DBG.log('processNode', { basePrice: basePrice, comparePrice: comparePrice, productId: productId, variantId: variantId, style: style, node: node });
    if (!basePrice || !productId) {
      DBG.warn('processNode skipped: missing basePrice or productId', node);
      return;
    }

    var info = getDiscountForVariant(resolved, productId, variantId);
    DBG.log('getDiscountForVariant result', info);
    if (!info) {
      DBG.log('product not eligible, leaving price untouched');
      return;
    }
    var discounted = computeDiscountedPriceCents(basePrice, info);
    DBG.log('computeDiscountedPriceCents result', { basePrice: basePrice, discounted: discounted });
    if (discounted == null || discounted === basePrice) {
      DBG.warn('discount produces no price change; leaving untouched');
      return;
    }

    try {
      if (style === 'bundle') {
        applyBundleStyle(node, basePrice, comparePrice, discounted);
      } else if (style === 'dpc') {
        applyDpcStyle(node, basePrice, comparePrice, discounted);
      } else {
        applyDefaultStyle(node, basePrice, comparePrice, discounted);
      }
      DBG.log('applied style', style, 'to node', node);
    } catch (e) {
      DBG.error('apply style failed; node left untouched', e);
    }
  }

  function processAll(resolved) {
    if (!resolved || !resolved.active || (resolved.type !== 'percentage' && resolved.type !== 'fixed')) {
      DBG.log('processAll: nothing to do (resolved inactive or unsupported type)', resolved);
      return;
    }
    var nodes = document.querySelectorAll('[data-dentis-price]');
    DBG.log('processAll: found ' + nodes.length + ' [data-dentis-price] nodes');
    for (var i = 0; i < nodes.length; i++) processNode(nodes[i], resolved);
  }

  function bindMutationObserver(resolved) {
    if (!('MutationObserver' in window)) return;
    if (!resolved || !resolved.active) return;
    var observer = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        if (!added) continue;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-dentis-price]')) {
            processNode(node, resolved);
          }
          if (node.querySelectorAll) {
            var inner = node.querySelectorAll('[data-dentis-price]');
            for (var i = 0; i < inner.length; i++) processNode(inner[i], resolved);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function handle(resolved) {
    try {
      processAll(resolved);
      bindMutationObserver(resolved);
    } catch (e) { DBG.error('handle() threw', e); }
  }

  document.addEventListener(EVENT_NAME, function (ev) {
    handle((ev && ev.detail) || window.DentisActiveDiscount);
  });

  // Defensive: if the engine dispatched before this listener attached
  // (cache-hit synchronous path), pick up the already-set state on next tick.
  function maybeReplay() {
    if (window.DentisActiveDiscount) {
      DBG.log('replay: window.DentisActiveDiscount already set on listener attach', window.DentisActiveDiscount);
      handle(window.DentisActiveDiscount);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeReplay);
  } else {
    setTimeout(maybeReplay, 0);
  }
})();
